import { FC, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { typedYupResolver } from '@eneco/hooks/form';
import { ButtonLink, Heading, Image, NotificationBox, RadioTile, Stack, Stretch, TextLink } from '@eneco/sparky';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import type { GenericRadioChoiceStepContent } from './generic-radio-choice-step-content.types';
import { FlowRadioGroup, Header, Layout, NextButton, getRadioGroupNameProps } from '../../components';
import { requiredRadioChoice } from '../../helpers/requiredRadioChoice';
import { useFlowRedirect, useStepContent, useStepKey } from '../../hooks';
import { FlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';
import { getIllustration } from '../../utils/getIllustration';

interface FormFields {
  [key: string]: string;
}

export const GenericRadioChoiceStep = () => {
  // This step won't re-render properly when used on multiple consecutive steps
  // without a key that changes per step page. See `useStepKey` for why the
  // rendering's `uid` is not that key.
  return <GenericRadioChoice key={useStepKey()} />;
};

const GenericRadioChoice: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks();
  const sendToFlowMachine = useFlowActorRef().send;
  const flowContext = useFlowSelector(state => state.context);
  const { customRedirect } = useFlowRedirect();
  const textualData = useStepContent<GenericRadioChoiceStepContent>('GenericRadioChoiceStep');

  const genericFieldName = textualData?.choice?.name ?? '';
  const enableRedirectLinkFlag = textualData?.choice?.enableRedirectLinkFlag;
  const requiredMessage =
    textualData?.choice?.requiredMessageText || textualData?.choice?.genericContentRadio?.requiredMessage;

  const formSchema = yup.object({
    [genericFieldName]: requiredRadioChoice(requiredMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      [genericFieldName]: flowContext[genericFieldName as keyof FlowContext]?.toString(),
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const [isPrimaryModalOpen, setIsPrimaryModalOpen] = useState(false);
  const [isFallbackModalOpen, setIsFallbackModalOpen] = useState(false);

  const mutatedGenericField = watch(genericFieldName);

  // Separate dialog contents
  const primaryDialog = textualData?.content?.moreInfoDialog;
  const fallbackDialog = textualData?.moreInfoDialog;

  const showNonBlockingNotification =
    mutatedGenericField === textualData?.choice?.nonBlockingValue &&
    textualData?.content?.nonBlockingChoiceNotification?.content;

  const showBlockingNotification =
    mutatedGenericField === textualData?.choice?.blockingValue &&
    textualData?.genericError?.errorBlockingChoiceNotification?.content;

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();
      let genericFieldValue: string | boolean | number = formData[genericFieldName];

      // Check if redirectLink should be used
      if (enableRedirectLinkFlag) {
        const selectedOption = textualData?.choice?.genericContentRadio?.options?.find(
          (option: { name?: string }) => option.name === genericFieldValue,
        );
        const redirectLink = selectedOption?.redirectLink;

        if (redirectLink?.href) {
          customRedirect(redirectLink.href, { cleanBasket: false });
          return;
        }
      }

      // Convert genericFieldValue to the appropriate type
      if (genericFieldValue.toLowerCase() === 'true' || genericFieldValue.toLowerCase() === 'false') {
        genericFieldValue = genericFieldValue.toLowerCase() === 'true';
      } else if (Number(genericFieldValue) || Number(genericFieldValue) === 0) {
        genericFieldValue = Number(genericFieldValue);
      }

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          [genericFieldName]: genericFieldValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        {textualData?.content?.title && <Header.Title>{textualData.content.title}</Header.Title>}

        {(textualData?.content?.content || primaryDialog?.triggerText || fallbackDialog?.triggerText) && (
          <Header.Text>
            <Stack gap="3" direction="column">
              <RichText html={textualData?.content?.content} />

              {primaryDialog?.title && primaryDialog?.title.length > 0 ? (
                // Show Primary Dialog if title length is greater than 0
                <TrackedDialog
                  isOpen={isPrimaryModalOpen}
                  setOpen={setIsPrimaryModalOpen}
                  trigger={<TextLink emphasis="low">{primaryDialog.triggerText}</TextLink>}>
                  <Stack gap="6" direction="column">
                    <Heading size="S" as="h2">
                      {primaryDialog.title}
                    </Heading>
                    <RichText html={primaryDialog.content} />
                  </Stack>
                </TrackedDialog>
              ) : (
                // Show Fallback Dialog if Primary Dialog title is empty or not defined
                fallbackDialog?.triggerText && (
                  <TrackedDialog
                    isOpen={isFallbackModalOpen}
                    setOpen={setIsFallbackModalOpen}
                    trigger={<TextLink emphasis="low">{fallbackDialog.triggerText}</TextLink>}>
                    <Stack gap="6" direction="column">
                      {fallbackDialog.image?.formats?.[0]?.src && (
                        <Image
                          src={fallbackDialog.image?.formats?.[0]?.src}
                          alt={fallbackDialog.image.alt || ''}
                          aspectRatio="16/9"
                          height="auto"
                          objectFit="cover"
                        />
                      )}
                      {fallbackDialog.title && (
                        <Heading size="S" as="h2">
                          {fallbackDialog.title}
                        </Heading>
                      )}
                      <RichText html={fallbackDialog.text} />
                      {fallbackDialog.buttonLink?.href && (
                        <Stack.Item>
                          <Stretch width={{ initial: true, lg: 'auto' }}>
                            <ButtonLink
                              action="primary"
                              target={fallbackDialog.buttonLink.target}
                              href={fallbackDialog.buttonLink.href}>
                              {fallbackDialog.buttonLink.text}
                            </ButtonLink>
                          </Stretch>
                        </Stack.Item>
                      )}
                    </Stack>
                  </TrackedDialog>
                )
              )}
            </Stack>
          </Header.Text>
        )}
      </Header>

      {textualData?.content?.notification?.content && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.content?.notification?.variant}
          title={textualData?.content?.notification?.title}
          text={<RichText html={textualData?.content?.notification?.content} />}
        />
      )}

      <FlowRadioGroup
        control={control}
        name={genericFieldName}
        {...getRadioGroupNameProps(textualData?.choice?.radioGroupLabel)}
        hint={textualData?.choice?.helpText || undefined}
        direction="column"
        error={errors?.[genericFieldName]?.message}>
        {textualData?.choice?.genericContentRadio?.options?.map(({ name, label, illustration }) => {
          const Illustration = getIllustration(illustration);

          return (
            <RadioTile
              key={name}
              value={name ?? ''}
              icon={Illustration && <Illustration size="small" color="currentColor" />}>
              {label ?? ''}
            </RadioTile>
          );
        })}
      </FlowRadioGroup>

      {showNonBlockingNotification && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.content?.nonBlockingChoiceNotification?.variant}
          title={textualData?.content?.nonBlockingChoiceNotification?.title}
          text={<RichText html={textualData?.content?.nonBlockingChoiceNotification?.content} />}
        />
      )}

      {!showBlockingNotification && (
        <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText}</NextButton>
      )}

      {showBlockingNotification && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.genericError?.errorBlockingChoiceNotification?.variant}
          title={textualData?.genericError?.errorBlockingChoiceNotification?.title}
          text={<RichText html={textualData?.genericError?.errorBlockingChoiceNotification?.content} />}
        />
      )}
    </Layout.Content>
  );
};
